import { computed, toRef, type ComputedRef, type Ref } from 'vue'
import { createGetItemIdFromKey, defaultGetItemId } from './gpx-select-option-utils'
import type { GpxSelectFieldKeys } from './useGpxSelectFieldKeys'
import { useGpxLocalSelect } from './useGpxLocalSelect'
import { useGpxRemoteSelect } from './useGpxRemoteSelect'
import { useGpxRemoteStaticSelect } from './useGpxRemoteStaticSelect'
import type { GpxRemoteSelectState } from '../remote-select/types'
import type { GpxRemoteSelectFetchPage } from '../remote-select/types'

const EMPTY_PAGE = {
  data: [],
  meta: { current_page: 1, last_page: 1, per_page: 100, total: 0 },
}

export type GpxSelectStateProps = {
  modelValue?: unknown
  options?: unknown[]
  remote?: boolean
  fetchPage?: GpxRemoteSelectFetchPage<unknown> | null
  fetchOptions?: ((args: { query: string }) => Promise<unknown[]>) | null
  perPage?: number
  debounceMs?: number
  itemSize?: number
  getItemId?: ((item: unknown) => string | number) | null
  resolveSelected?: ((value: unknown) => Promise<unknown | null>) | null
}

type SelectModeState = GpxRemoteSelectState<unknown> & {
  cacheSelected: (item: unknown | null) => void
}

export function useGpxSelectState(
  props: GpxSelectStateProps,
  fieldKeys: ComputedRef<GpxSelectFieldKeys>,
) {
  const modelValueRef = toRef(props, 'modelValue') as Ref<unknown>
  const optionsRef = toRef(props, 'options') as Ref<unknown[]>

  const remotePaginatedEnabled = computed(
    () => props.remote && typeof props.fetchPage === 'function',
  )
  const remoteStaticEnabled = computed(
    () =>
      props.remote &&
      typeof props.fetchOptions === 'function' &&
      !remotePaginatedEnabled.value,
  )
  const remoteEnabled = computed(
    () => remotePaginatedEnabled.value || remoteStaticEnabled.value,
  )

  const effectiveGetItemId = computed(
    () =>
      props.getItemId ??
      (fieldKeys.value.effectiveValueKey
        ? createGetItemIdFromKey(fieldKeys.value.effectiveValueKey)
        : defaultGetItemId),
  )

  const paginatedState = useGpxRemoteSelect({
    fetchPage: props.fetchPage ?? (async () => EMPTY_PAGE),
    perPage: props.perPage ?? 100,
    debounceMs: props.debounceMs ?? 300,
    itemSize: props.itemSize ?? 38,
    getItemId: (item) => effectiveGetItemId.value(item),
    modelValue: modelValueRef,
    resolveSelected: props.resolveSelected ?? undefined,
    enabled: remotePaginatedEnabled,
  })

  const staticState = useGpxRemoteStaticSelect({
    fetchOptions: props.fetchOptions ?? (async () => []),
    debounceMs: props.debounceMs ?? 300,
    getItemId: (item) => effectiveGetItemId.value(item),
    modelValue: modelValueRef,
    resolveSelected: props.resolveSelected ?? undefined,
    enabled: remoteStaticEnabled,
  })

  const localState = useGpxLocalSelect({
    options: optionsRef,
    modelValue: modelValueRef,
    getItemId: (item) => effectiveGetItemId.value(item),
    resolveSelected: props.resolveSelected ?? undefined,
  })

  const activeState = computed<SelectModeState>(() => {
    if (remotePaginatedEnabled.value) {
      return paginatedState as SelectModeState
    }
    if (remoteStaticEnabled.value) {
      return staticState as SelectModeState
    }
    return localState as SelectModeState
  })

  const effectiveOptions = computed(() => activeState.value.options.value)
  const effectiveLoading = computed(() => activeState.value.loading.value)
  const effectiveShowEmptyMessage = computed(
    () => activeState.value.showEmptyMessage?.value ?? false,
  )
  const effectiveResolvedSelected = computed(
    () => activeState.value.resolvedSelected?.value ?? null,
  )
  const effectiveVirtualScrollerOptions = computed(() => {
    if (remotePaginatedEnabled.value) {
      return paginatedState.virtualScrollerOptions.value
    }
    return undefined
  })

  const remoteFilterQuery = computed(() => {
    if (remotePaginatedEnabled.value) {
      return paginatedState.filterQuery.value
    }
    if (remoteStaticEnabled.value) {
      return staticState.filterQuery.value
    }
    return ''
  })

  function onFilterHandler(event: { value: string }) {
    activeState.value.onFilter(event)
  }

  function onOverlayShowHandler() {
    activeState.value.onOverlayShow()
  }

  function cacheSelected(item: unknown | null) {
    activeState.value.cacheSelected(item)
  }

  return {
    remoteEnabled,
    effectiveOptions,
    effectiveLoading,
    effectiveShowEmptyMessage,
    effectiveResolvedSelected,
    effectiveVirtualScrollerOptions,
    remoteFilterQuery,
    onFilterHandler,
    onOverlayShowHandler,
    cacheSelected,
  }
}
